import { ref, watch } from '@vue/composition-api'
import { debounce } from 'lodash'
import { errorNotification } from '@/utils/toast-notifications'
import axiosIns from '@/libs/axios'

function deepMerge(target, source) {
  const result = Array.isArray(target) ? [...target] : { ...target }

  Object.keys(target).forEach(key => {
    if (Object.prototype.hasOwnProperty.call(source, key)) {
      const targetVal = target[key]
      const sourceVal = source[key]

      if (
        targetVal
        && typeof targetVal === 'object'
        && !Array.isArray(targetVal)
        && sourceVal
        && typeof sourceVal === 'object'
        && !Array.isArray(sourceVal)
      ) {
        result[key] = deepMerge(targetVal, sourceVal)
      } else {
        result[key] = sourceVal
      }
    }
  })

  return result
}

export default function useChallengeForm(props, ctx) {
  const fromWeekDays = ref(
    Array.from({ length: 7 }, (_, i) => ({
      value: i,
      text: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][i],
    })),
  )

  const toWeekDays = ref(
    Array.from({ length: 7 }, (_, i) => ({
      value: i,
      text: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][i],
      disabled: false,
    })),
  )

  const recurringType = ref('daily')

  const roleOptions = [
    {
      label: 'Driver',
      value: 'DRIVER',
    },
    {
      label: 'Rider',
      value: 'USER',
    },
  ]
  const rewardType = ref('promo') // 'wallet' or 'promo'
  const sendNotification = ref(false)
  const enableTiers = ref(false)
  const tiers = ref([
    {
      numberOfTrips: 1,
      actions: ['ADD_TO_GROUP'],
      group: null,
      amount: null,
      notification: {
        title: {
          en: null,
          ar: null,
        },
        description: {
          en: null,
          ar: null,
        },
      },
    },
  ])
  const rewardForm = ref({
    name: {
      en: null,
      ar: null,
    },
    promoCode: null,
    amount: null,
    group: null,
    targetedGroup: null,
    effectiveTime: null,
    endTime: null,
    notification: {
      title: {
        en: null,
        ar: null,
      },
      description: {
        en: null,
        ar: null,
      },
    },
    description: {
      en: null,
      ar: null,
    },
    triggers: [
      'riden.billing.bill-generated',
    ],
    actions: [
      'ADD_TO_GROUP',
    ],
    criteria: {
      role: 'USER',
      type: 'NUMBER_OF_TRIPS',
      daily: false,
      weekly: false,
      fromDate: null,
      toDate: null,
      monthly: false,
      numberOfTrips: null,
      fromHour: null,
      toHour: null,
      weekStartDay: null,
      weekEndDay: null,
      monthStartDate: null,
      monthEndDate: null,
      pickupZone: [],
      recurring: true,
    },
  })
  const fetchingReward = ref(false)
  const rewardsFormRef = ref(null)
  const zonesList = ref([])
  const fetchingZones = ref(false)
  const fetchZones = async () => {
    fetchingZones.value = true
    const res = await ctx.root.$store.dispatch('zones/fetchZones')
    fetchingZones.value = false
    zonesList.value = res
  }
  const groupsList = ref([])
  const fetchingGroups = ref(false)

  const fetchGroups = debounce(async (search, loading) => {
    if (!search) return
    loading(true)

    const result = await axiosIns.get('/groups', {
      params: {
        name: search,
      },
    })
    const { data } = result.data.data
    groupsList.value = data
    loading(false)
  }, 1000)

  fetchZones()
  const rewardID = ctx.root.$route.params.id
  const getReward = async () => {
    try {
      fetchingReward.value = true
      const res = await ctx.root.$store.dispatch('rewards/getReward', rewardID)
      rewardForm.value = deepMerge(rewardForm.value, res.data.data.data)
      sendNotification.value = !!rewardForm.value.notification

      // Handle tiers data loading
      const responseData = res.data.data.data
      if (responseData.tiers && Array.isArray(responseData.tiers)) {
        enableTiers.value = true
        tiers.value = responseData.tiers.map(tier => ({
          numberOfTrips: tier.numberOfTrips,
          actions: [...tier.actions],
          group: tier.group || null,
          amount: tier.amount || null,
          notification: tier.notification || {
            title: { en: null, ar: null },
            description: { en: null, ar: null },
          },
        }))
        // Set criteria numberOfTrips to 10 when tiers is enabled
        rewardForm.value.criteria.numberOfTrips = 10
      } else {
        enableTiers.value = false
      }
      rewardForm.value.effectiveTime = new Date(rewardForm.value.effectiveTime)
      rewardForm.value.endTime = new Date(rewardForm.value.endTime)
      if (rewardForm.value.criteria.fromDate && rewardForm.value.criteria.toDate) {
        rewardForm.value.criteria.fromDate = new Date(rewardForm.value.criteria.fromDate)
        rewardForm.value.criteria.toDate = new Date(rewardForm.value.criteria.toDate)
      }
      if (rewardForm.value.actions.includes('CREDIT_TO_WALLET')) {
        rewardType.value = 'wallet'
      } else {
        rewardType.value = 'promo'
      }
      if (rewardForm.value.criteria.daily) recurringType.value = 'daily'
      else if (rewardForm.value.criteria.weekly) recurringType.value = 'weekly'
      else if (rewardForm.value.criteria.monthly) recurringType.value = 'monthly'
      else recurringType.value = 'custom'
    } catch (error) {
      errorNotification('An error occured')
    } finally {
      fetchingReward.value = false
    }
  }
  if (rewardID) {
    getReward()
  }
  const submitting = ref(false)
  const submitHandler = async () => {
    if (+rewardForm.value.criteria.numberOfTrips < 0) {
      errorNotification('Number of trips cannot be negative')
      return
    }
    if (!enableTiers.value && !rewardForm.value.group && rewardType.value === 'promo') {
      errorNotification('The group field is required')
      return
    }
    const valid = await rewardsFormRef.value.validate()
    if (!valid) return
    submitting.value = true
    const temp = {
      name: rewardForm.value.name,
      amount: rewardForm.value.amount,
      promoCode: rewardForm.value.promoCode || undefined,
      group: rewardType.value === 'promo' ? rewardForm.value.group : undefined,
      targetedGroup: rewardForm.value.targetedGroup,
      description: rewardForm.value.description,
      triggers: rewardForm.value.triggers,
      actions: JSON.parse(JSON.stringify(rewardForm.value.actions)),
      effectiveTime: rewardForm.value.effectiveTime,
      endTime: rewardForm.value.endTime,
      criteria: {
        role: 'USER',
        type: 'NUMBER_OF_TRIPS',
        numberOfTrips: enableTiers.value ? 10 : rewardForm.value.criteria.numberOfTrips,
        pickupZone: rewardForm.value.criteria.pickupZone,
        recurring: true,
      },
    }

    // Add tiers field if enabled
    if (enableTiers.value) {
      // Validate tiers before submission
      const tiersValid = tiers.value.every(tier => {
        if (tier.numberOfTrips < 1) return false
        if (tier.actions.includes('ADD_TO_GROUP') && !tier.group) return false
        if (tier.actions.includes('CREDIT_TO_WALLET') && (!tier.amount || tier.amount < 0)) return false
        if (tier.actions.includes('SEND_NOTIFICATION')) {
          if (!tier.notification.title.en || !tier.notification.title.ar) return false
          if (!tier.notification.description.en || !tier.notification.description.ar) return false
        }
        return true
      })

      if (!tiersValid) {
        errorNotification('Please fill all required tier fields correctly')
        submitting.value = false
        return
      }

      // Transform tiers to the expected structure
      temp.tiers = tiers.value.map(tier => {
        const tierObj = {
          numberOfTrips: tier.numberOfTrips,
          actions: [...tier.actions],
        }

        // Add group if ADD_TO_GROUP is in actions
        if (tier.actions.includes('ADD_TO_GROUP')) {
          tierObj.group = tier.group
        }

        // Add amount if CREDIT_TO_WALLET is in actions
        if (tier.actions.includes('CREDIT_TO_WALLET')) {
          tierObj.amount = tier.amount
        }

        // Add notification if SEND_NOTIFICATION is in actions
        if (tier.actions.includes('SEND_NOTIFICATION')) {
          tierObj.notification = tier.notification
        }

        return tierObj
      })

      // Remove fields not needed for tiers structure
      delete temp.amount
      delete temp.promoCode
      delete temp.group
      delete temp.targetedGroup
      delete temp.actions
      delete temp.notification

      // Add required fields for tiers
      temp.country = 'JO'
      temp.expireAt = new Date(rewardForm.value.endTime).toISOString()
    }

    // Only apply recurring logic when tiers is not enabled
    if (recurringType.value === 'daily') {
      temp.criteria.daily = true
      temp.criteria.fromHour = rewardForm.value.criteria.fromHour
      temp.criteria.toHour = rewardForm.value.criteria.toHour
    } else if (recurringType.value === 'weekly') {
      temp.criteria.daily = false
      temp.criteria.weekly = true
      temp.criteria.weekStartDay = rewardForm.value.criteria.weekStartDay
      temp.criteria.weekEndDay = rewardForm.value.criteria.weekEndDay
    } else if (recurringType.value === 'monthly') {
      temp.criteria.daily = false
      temp.criteria.monthly = true
      temp.criteria.monthStartDate = rewardForm.value.criteria.monthStartDate
      temp.criteria.monthEndDate = rewardForm.value.criteria.monthEndDate
    } else {
      temp.criteria.recurring = false
      temp.criteria.daily = false
      temp.criteria.fromDate = new Date(rewardForm.value.criteria.fromDate).toISOString()
      temp.criteria.toDate = new Date(rewardForm.value.criteria.toDate).toISOString()
    }

    // Only apply notification logic when tiers is not enabled
    if (!enableTiers.value) {
      if (sendNotification.value) {
        temp.notification = rewardForm.value.notification
        temp.actions.push('SEND_NOTIFICATION')
      } else {
        const index = temp.actions.indexOf('SEND_NOTIFICATION')
        if (index !== -1) {
          temp.actions.splice(index, 1)
        }
      }
    }
    temp.effectiveTime = new Date(temp.effectiveTime).toISOString()
    temp.endTime = new Date(temp.endTime).toISOString()

    try {
      if (props.isNew) {
        await ctx.root.$store.dispatch('rewards/createReward', temp)
      } else {
        await ctx.root.$store.dispatch('rewards/patchReward', {
          data: temp,
          id: ctx.root.$route.params.id,
        })
      }
      ctx.root.$router.push('/challenges')
    } catch (error) {
      console.log(error)
      errorNotification('An error occured')
    } finally {
      submitting.value = false
    }
  }
  const numberOfTripsInput = ev => {
    setTimeout(() => {
      const temp = String(ev.key)
      if (temp === '+' || temp === 'e') rewardForm.value.criteria.numberOfTrips = 0
      if (!ev) rewardForm.value.criteria.numberOfTrips = 0
    }, 0)
  }

  const addTier = () => {
    tiers.value.push({
      numberOfTrips: Math.max(...tiers.value.map(t => t.numberOfTrips)) + 1,
      actions: ['ADD_TO_GROUP'],
      group: null,
      amount: null,
      notification: {
        title: {
          en: null,
          ar: null,
        },
        description: {
          en: null,
          ar: null,
        },
      },
    })
  }

  const removeTier = index => {
    if (tiers.value.length > 1) {
      tiers.value.splice(index, 1)
    }
  }

  watch(rewardType, newType => {
    if (newType === 'wallet') {
      rewardForm.value.actions = ['CREDIT_TO_WALLET']
    } else {
      rewardForm.value.actions = ['ADD_TO_GROUP']
    }
  })

  watch(enableTiers, newValue => {
    if (newValue) {
      // When tiers is enabled, set numberOfTrips to 10
      rewardForm.value.criteria.numberOfTrips = 10
    }
  })

  return {
    fromWeekDays,
    toWeekDays,
    groupsList,
    recurringType,
    numberOfTripsInput,
    submitHandler,
    rewardsFormRef,
    fetchingGroups,
    rewardForm,
    submitting,
    roleOptions,
    fetchGroups,
    zonesList,
    fetchingZones,
    fetchingReward,
    rewardType,
    sendNotification,
    enableTiers,
    tiers,
    addTier,
    removeTier,
  }
}
